import { latLonToVec3 } from '../geo/latlon';
import type { LatLon } from '../geo/types';

/**
 * Great-circle path generation. Given two lat/lon endpoints, produce a list
 * of vec3 points along the spherical interpolation between them, with an
 * optional radial "bow" so the arc visually rises off the sphere.
 *
 * The bow is what makes the arc readable in 3D — without it, the line lies
 * flat on the surface and is hard to distinguish from borders / fills /
 * texture content. With it, the arc reads as "traveling" from A to B in
 * space.
 */

export interface GreatCircleOptions {
  /** Number of vertices along the arc (segments + 1). Default 64. */
  segments?: number;
  /**
   * Maximum lift above the unit sphere at the arc's midpoint. Endpoints
   * always sit at radius 1 (well, `surfaceRadius`). Default: auto-scaled
   * by arc length so a short arc doesn't tower up and a long one doesn't
   * lie flat.
   */
  arcHeight?: number;
  /** Sphere radius. Default 1 (unit sphere — matches the engine's globe). */
  surfaceRadius?: number;
}

/**
 * Returns `segments + 1` vec3 points sampling the great circle between
 * `from` and `to`. The middle of the arc bows up by `arcHeight` so the
 * arc sits visibly above the globe surface.
 */
export function greatCirclePath(
  from: LatLon,
  to: LatLon,
  options: GreatCircleOptions = {},
): Array<{ x: number; y: number; z: number }> {
  const segments = options.segments ?? 64;
  const surfaceRadius = options.surfaceRadius ?? 1;

  const a = latLonToVec3(from.lat, from.lon, 1);
  const b = latLonToVec3(to.lat, to.lon, 1);

  // Angular separation between the two endpoints. Acos clamped to avoid NaN
  // from floating-point drift slightly outside [-1, 1].
  const cosTheta = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z));
  const theta = Math.acos(cosTheta);

  // Auto-scale arc height by angular distance: tiny arcs get tiny bows
  // (they'd look comically arched otherwise), long arcs bow further so
  // they read as a 3D path rather than a chord under the sphere.
  const arcHeight =
    options.arcHeight ?? Math.max(0.005, Math.min(0.2, theta * 0.08));

  const points: Array<{ x: number; y: number; z: number }> = [];

  // Degenerate case: endpoints are the same point. Single-vertex output.
  if (theta < 1e-6) {
    points.push({
      x: a.x * surfaceRadius,
      y: a.y * surfaceRadius,
      z: a.z * surfaceRadius,
    });
    return points;
  }

  const sinTheta = Math.sin(theta);
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    // SLERP weights — produces a unit-sphere point along the great circle.
    const ka = Math.sin((1 - t) * theta) / sinTheta;
    const kb = Math.sin(t * theta) / sinTheta;
    const ux = ka * a.x + kb * b.x;
    const uy = ka * a.y + kb * b.y;
    const uz = ka * a.z + kb * b.z;
    // Bow: radial scale peaks at t=0.5 and is exactly `surfaceRadius` at
    // both endpoints (sin(0) = sin(π) = 0).
    const lift = surfaceRadius * (1 + arcHeight * Math.sin(Math.PI * t));
    points.push({ x: ux * lift, y: uy * lift, z: uz * lift });
  }
  return points;
}
